import { useCallback, useEffect, useState } from 'react';
import { SustainabilityReportOptions } from 'src/shared/services/api/options/sustainabilityReportOptions';
import { GridRowParams, GridSortModel } from '@mui/x-data-grid-premium';
import { SustainabilityAPI } from 'src/shared/services/api/sustainabilityAPI';
import { useIsMounted } from 'src/shared/helpers/hooks/useIsMounted';
import { SustainabilityReportResponse } from 'src/shared/services/api/response/sustainabilityReportResponse';
import { BusinessAreaResponse } from 'src/shared/services/api/response/businessAreasResponse';
import { TagsResponse } from 'src/shared/services/api/response/tagsResponse';
import ConsoleHelper from 'src/shared/helpers/consoleHelper';
import { useHistory } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/enums/routes.enum';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { LocalStorageKeysEnum } from 'src/shared/enums/localStorageKeys.enum';
import { useLocalStorage } from 'src/shared/helpers/hooks/useLocalStorage';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { RootStateType } from 'src/shared/services/redux/reducers';
import { SustainabilityDataGrid } from './sustainabilityDataGrid';
import {
  ProjectStatusFilter,
  SustainabilityFilter,
} from './sustainabilityFilter';
import { UncontrolledCheckBox } from '../form/uncontrolledCheckBox';

export const SustainabilityCertifiedProject = () => {
  const isMounted = useIsMounted();
  const history = useHistory();
  const { t } = useTranslation();

  const { userDetails } = useSelector(
    (state: RootStateType) => state.authState,
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<SustainabilityReportResponse[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [businessArea, setBusinessArea] = useLocalStorage<
    BusinessAreaResponse | undefined
  >(LocalStorageKeysEnum.SustainabilityReportProjectBusinessArea, undefined);
  const [locationTag, setLocationTag] = useLocalStorage<
    TagsResponse | undefined
  >(LocalStorageKeysEnum.SustainabilityReportProjectTagsResponse, undefined);
  const [year, setYear] = useLocalStorage<ProjectStatusFilter | undefined>(
    LocalStorageKeysEnum.SustainabilityReportProjectYear,
    {
      id: dayjs(new Date()).year(),
      name: dayjs(new Date()).year().toString(),
    },
  );
  const [reportedByMe, setReportedByMe] = useLocalStorage<boolean>(
    LocalStorageKeysEnum.SustainabilityReportReportedByMe,
    false,
  );
  const [includeCustomerSurveyExport, setIncludeCustomerSurveyExport] =
    useState<boolean>(false);

  const [requestOptions, setRequestOptions] =
    useState<SustainabilityReportOptions>({
      pageIndex: 0,
      pageSize: 100,
      sortCol: 'created',
      sortDir: 'asc',
      reporterUserId: reportedByMe && userDetails ? userDetails.id : undefined,
      locationTagId: locationTag?.id,
      businessAreaID: businessArea?.id,
      projectStatus: [3],
      filterDate: year && `${year.name}-01-01`,
    });

  const fetchSustainabilityReports = useCallback(async () => {
    setLoading(true);
    const api = new SustainabilityAPI();

    try {
      const res = await api.GetAll(requestOptions);
      if (isMounted()) {
        setData(res.results ?? []);
        setTotalItems(res.totalItems ?? 0);
        ConsoleHelper.log('RES fetchSustainabilityReports ', res);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.status === 404) {
        setData([]);
        setTotalItems(0);
      }
      ConsoleHelper.log('ERROR fetchSustainabilityReports ', error);
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, requestOptions]);

  // Sync requestOptions when local storage changes
  useEffect(() => {
    setRequestOptions((prev) => ({
      ...prev,
      locationTagId: locationTag?.id,
      businessAreaID: businessArea?.id,
      filterDate: year && `${year.name}-01-01`,
      reporterUserId: reportedByMe && userDetails ? userDetails.id : undefined,
    }));
  }, [businessArea?.id, locationTag?.id, reportedByMe, userDetails, year]);

  useEffect(() => {
    fetchSustainabilityReports();
  }, [fetchSustainabilityReports]);

  // Byt sida
  const onPageChange = (newPage: number) => {
    setRequestOptions((prev) => ({
      ...prev,
      pageIndex: newPage,
    }));
  };

  // Ändra antal per sida
  const onPageSizeChange = (size: number) => {
    setRequestOptions((prev) => ({
      ...prev,
      pageSize: size,
    }));
  };

  // TODO: apiet accepterar inte sortCol: model[0].field,
  const onSortModelChanged = (model: GridSortModel) => {
    if (model.length > 0)
      setRequestOptions((prev) => ({
        ...prev,
        sortDir: model[0].sort as string,
      }));
  };

  const onRowClickHandler = (params: GridRowParams) => {
    history.push(
      `${RoutesEnum.SustainabilityReportDetailsPage}/${params.row.id}`,
    );
  };

  return (
    <>
      <Typography mb={2}>{t('sustainabilityCertifiedInfo')}</Typography>
      <SustainabilityFilter
        businessArea={businessArea}
        setBusinessArea={setBusinessArea}
        locationTag={locationTag}
        setLocations={setLocationTag}
        year={year}
        setYear={setYear}
        reportedByMe={reportedByMe}
        setReportedByMe={setReportedByMe}
      />
      <SustainabilityDataGrid
        loading={loading}
        data={data ?? []}
        totalRows={totalItems ?? 0}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChanged={onSortModelChanged}
        page={requestOptions.pageIndex ?? 0}
        size={requestOptions.pageSize ?? 100}
        onRowClick={onRowClickHandler}
        includeCustomerSurveyExport={includeCustomerSurveyExport}
        toolbarContent={
          <UncontrolledCheckBox
            label={t('includeCustomerSurveyExport')}
            labelPlacement='end'
            size='small'
            labelVariant='body2'
            color='text.secondary'
            sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
            checked={includeCustomerSurveyExport}
            onChange={setIncludeCustomerSurveyExport}
          />
        }
      />
    </>
  );
};
